import React from 'react';
import { Box, CircularProgress, CircularProgressProps, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Delete, Error, KeyboardArrowDown, KeyboardArrowUp, KeyboardDoubleArrowUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';


interface IListingImageThumbnailProps {
    name: string;
    description?: string;
    url: string;
    percent?: number;
    error?: Error;
    onDeleteClick?: () => void;
}

export const ListingImageThumbnail = ({
    name,
    description,
    url,
    percent,
    error,
    onDeleteClick,
}: IListingImageThumbnailProps) => {
    return (
        <Box sx={{
            position: 'relative',
            borderRadius: 2,
            border: '1px solid #eaeaea',
            padding: 1,
            // backgroundColor: 'red',
            height: '100%',
        }}>
            <Box sx={{ height: '100%' }}>
                <img
                    src={url}
                    style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        maxHeight: 300,
                        minHeight: 100,
                        objectFit: 'contain',
                        pointerEvents: 'none', // Prevents image from interfering with drag
                    }}
                    alt={!error ? (description || name) : error.message}
                    // Revoke data uri after image is loaded
                    onLoad={() => { URL.revokeObjectURL(url) }}
                />
                <Box sx={{
                    display: 'flex',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    {(percent !== undefined && percent < 100) ?
                        <CircularProgressWithLabel value={percent} />
                        :
                        onDeleteClick ?
                            <IconButton aria-label="delete"
                                size="large"
                                color={error ? 'error' : 'primary'}
                                onClick={onDeleteClick}>
                                {!!error ? <Error /> : <Delete />}
                            </IconButton>
                            : null
                    }
                </Box>
            </Box>
        </Box>
    )
}

interface IListingImageThumbnailEditProps extends IListingImageThumbnailProps {
    /** Callback on arrow up (to order one up) */
    onOrderUpClick?: () => void;
    /** Callback on arrow down (to order one down) */
    onOrderDownClick?: () => void;
    /** Callback on top up (to order all the way up) */
    onOrderTopClick?: () => void;
    /** Callback on description changed */
    onDescriptionValueChanged: (newDescription: string) => void;
}
export const ListingImageThumbnailEdit = ({
    name,
    description,
    url,
    percent,
    error,
    onDeleteClick,
    onOrderUpClick,
    onOrderDownClick,
    onOrderTopClick,
    onDescriptionValueChanged,
}: IListingImageThumbnailEditProps) => {
    const { t } = useTranslation();
    return (
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
            {(percent !== undefined && percent < 100) ?
                <CircularProgressWithLabel value={percent} />
                :
                onDeleteClick ?
                    <IconButton aria-label="delete"
                        size="large"
                        color={!!error ? 'error' : 'primary'}
                        onClick={onDeleteClick}>
                        {!!error ? <Error /> : <Delete />}
                    </IconButton>
                    : null
            }
            <Box sx={{ flex: 2 }}>
                {!!error ?
                    <Typography>{error.message}</Typography>
                    : <img
                        src={url}
                        style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            maxHeight: 200,
                            minHeight: 50,
                            objectFit: 'contain'
                        }}
                        alt={description || name}
                        // Revoke data uri after image is loaded
                        onLoad={() => { URL.revokeObjectURL(url) }}
                    />}
            </Box>
            {!error && <TextField sx={{ flex: 3 }}
                multiline={true}
                placeholder={t('listing.edit.photoDescriptionInputPlaceholder', { defaultValue: 'Description image' })}
                value={description}
                onChange={(e) => onDescriptionValueChanged(e.target.value)} />}
            <IconButton aria-label="delete" size="large" color='primary'
                onClick={onOrderTopClick}
                disabled={!onOrderTopClick}>
                <KeyboardDoubleArrowUp />
            </IconButton>
            <IconButton aria-label="delete" size="large" color='primary'
                onClick={onOrderUpClick}
                disabled={!onOrderUpClick}>
                <KeyboardArrowUp />
            </IconButton>
            <IconButton aria-label="delete" size="large" color='primary'
                onClick={onOrderDownClick}
                disabled={!onOrderDownClick}>
                <KeyboardArrowDown />
            </IconButton>
        </Stack>
    )
}

function CircularProgressWithLabel(
    props: CircularProgressProps & { value: number },
) {
    return (
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress variant="determinate" {...props} />
            <Box
                sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography
                    variant="caption"
                    component="div"
                    color="text.secondary">
                    {`${Math.round(props.value)}%`}
                </Typography>
            </Box>
        </Box>
    );
}
